import {
  AuthType,
  EndDateConfigKind,
  FieldClass,
  FieldType,
  IBasicConnectionInput,
  IBasicConnectionMeta,
  IBasicConnectionMetaFieldsFragment,
  IBigQueryConnectionInput,
  IBigQueryConnectionMeta,
  IBigQueryConnectionMetaFieldsFragment,
  IColumnDefinitionMetaFieldsFragment,
  IConnectionInput,
  ICustomColumnDefinitionInput,
  IDataModelAddDerivedFieldInput,
  IDataModelAddFieldInput,
  IDataModelExtendFactFieldInput,
  IDataModelFieldTypeInput,
  IDataModelJoinPairInput,
  IDataModelTemporalFieldRefsFieldsFragment,
  IDataModelUpdateFieldInput,
  IDataRefreshConfigMetaFieldsFragment,
  IDataSourceInput,
  IDerivedFieldDefinitionInput,
  IEndDateConfigInput,
  IEndDateConfigMeta,
  IFieldTypeParamsFieldsFragment,
  IFieldTypeParamsInput,
  IMapDefinitionMetaFieldsFragment,
  ISchemaDataModelFieldsFragment,
  ISchemaDsConfigFieldsFragment,
  ISchemaFieldMetaFieldsFragment,
  IStructDefinitionMetaFieldsFragment,
  ITableColumnFieldsFragment,
  ITableFieldFieldsFragment,
  ITableJoinMetaFieldsFragment,
  ITableJoinPairMeta,
  ITemporalJoinConditionMeta,
  StrategyKind,
  WeekStart,
} from '../../../utils/apollo/graphqlSchema';
import {
  IDataModelerDraftFieldData,
  IFieldTypeConfig,
  IJoinDraftData,
  ITemporalJoinConditionDraftData,
} from './types';
import moment from 'moment/moment';
import { INTERNAL_DATE_FORMAT } from '../../../config/constants';
import { listToHashMapFromKey } from '../../../pages/DataModeler/DataModelerSchema/utils';
import { isEqual } from 'lodash';
import { UnreachableCaseError } from 'ts-essentials';
import { ICdwTableColumnData } from '../../../pages/DataModeler/DataModelerSchema/DataModelerCdwTableGrid/types';
import { MaybeNil } from '../../../utils/interfaces';

export const DATA_MODEL_FIELD_TYPE_CONFIG: Record<FieldType, IFieldTypeConfig> =
  {
    [FieldType.Array]: {
      humanName: 'Array',
      covertsTo: [],
      canExtend: [FieldType.Array, FieldType.ArrayObject],
      canDefine: true,
      canBeArrayElement: false,
      defaultGroupby: true,
    },
    [FieldType.ArrayObject]: {
      humanName: 'Array',
      covertsTo: [],
      canExtend: [FieldType.Array, FieldType.ArrayObject],
      canDefine: false,
      canBeArrayElement: false,
      defaultGroupby: true,
    },
    [FieldType.Bool]: {
      humanName: 'Boolean',
      covertsTo: [],
      canExtend: [FieldType.Bool],
      canDefine: true,
      canBeArrayElement: true,
      defaultGroupby: true,
    },
    [FieldType.Date]: {
      humanName: 'Date',
      covertsTo: [],
      canExtend: [
        FieldType.Date,
        FieldType.DayOfWeek,
        FieldType.Week,
        FieldType.FourWeek,
        FieldType.Month,
        FieldType.Quarter,
        FieldType.Year,
      ],
      canDefine: true,
      canBeArrayElement: false,
      defaultGroupby: false,
    },
    [FieldType.DayOfWeek]: {
      humanName: 'DayOfWeek',
      covertsTo: [],
      canExtend: [FieldType.DayOfWeek],
      canDefine: false,
      canBeArrayElement: false,
      defaultGroupby: true,
    },
    [FieldType.Double]: {
      humanName: 'Double',
      covertsTo: [FieldType.Double, FieldType.Float, FieldType.String],
      canExtend: [FieldType.Double],
      canDefine: true,
      canBeArrayElement: true,
      defaultGroupby: false,
    },
    [FieldType.Float]: {
      humanName: 'Float',
      covertsTo: [FieldType.Float, FieldType.Double, FieldType.String],
      canExtend: [FieldType.Float],
      canDefine: true,
      canBeArrayElement: true,
      defaultGroupby: false,
    },
    [FieldType.FourWeek]: {
      humanName: 'FourWeek',
      covertsTo: [],
      canExtend: [FieldType.FourWeek],
      canDefine: false,
      canBeArrayElement: false,
      defaultGroupby: false,
    },
    [FieldType.HourOfDay]: {
      humanName: 'HourOfDay',
      covertsTo: [],
      canExtend: [FieldType.HourOfDay, FieldType.Integer],
      canDefine: false,
      canBeArrayElement: false,
      defaultGroupby: true,
    },
    [FieldType.Integer]: {
      humanName: 'Integer',
      covertsTo: [
        FieldType.Integer,
        FieldType.Double,
        FieldType.Float,
        FieldType.String,
      ],
      canExtend: [FieldType.Integer],
      canDefine: true,
      canBeArrayElement: true,
      defaultGroupby: true,
    },
    [FieldType.Json]: {
      humanName: 'JSON',
      covertsTo: [FieldType.Json, FieldType.String],
      canExtend: [FieldType.Json],
      canDefine: true,
      canBeArrayElement: true,
      defaultGroupby: true,
    },
    [FieldType.Month]: {
      humanName: 'Month',
      covertsTo: [],
      canExtend: [FieldType.Month],
      canDefine: false,
      canBeArrayElement: false,
      defaultGroupby: false,
    },
    [FieldType.Quarter]: {
      humanName: 'Quarter',
      covertsTo: [],
      canExtend: [FieldType.Quarter],
      canDefine: false,
      canBeArrayElement: false,
      defaultGroupby: false,
    },
    [FieldType.String]: {
      humanName: 'String',
      covertsTo: [FieldType.String, FieldType.Json],
      canExtend: [FieldType.String],
      canDefine: true,
      canBeArrayElement: true,
      defaultGroupby: true,
    },
    [FieldType.Struct]: {
      humanName: 'Struct',
      covertsTo: [],
      canExtend: [FieldType.Struct],
      canDefine: false,
      canBeArrayElement: false, // TODO
      defaultGroupby: true,
    },
    [FieldType.Map]: {
      humanName: 'Map',
      covertsTo: [],
      canExtend: [FieldType.Map],
      canDefine: false,
      canBeArrayElement: false, // TODO
      defaultGroupby: true,
    },
    [FieldType.Timestamp]: {
      humanName: 'Timestamp',
      covertsTo: [],
      canExtend: [FieldType.Timestamp],
      canDefine: true,
      canBeArrayElement: false,
      defaultGroupby: false,
    },
    [FieldType.Week]: {
      humanName: 'Week',
      covertsTo: [],
      canExtend: [FieldType.Week],
      canDefine: false,
      canBeArrayElement: false,
      defaultGroupby: false,
    },
    [FieldType.Year]: {
      humanName: 'Year',
      covertsTo: [],
      canExtend: [FieldType.Year],
      canDefine: false,
      canBeArrayElement: false,
      defaultGroupby: false,
    },
    [FieldType.Datetime]: {
      humanName: 'DateTime',
      covertsTo: [],
      canExtend: [],
      canDefine: false,
      canBeArrayElement: false,
      defaultGroupby: false,
    },
  };

export const customColumnDefinitionToInput = (
  def: IColumnDefinitionMetaFieldsFragment | null,
): ICustomColumnDefinitionInput | null => {
  if (!def) {
    return null;
  }
  if (def.__typename === 'MappedStringDefinitionMeta') {
    return {
      rawSql: null,
      mappedString: {
        mapElse: def.mapElse,
        mapNull: def.mapNull,
        mappedColumn: def.mappedColumn,
        values: def.values.map((mapValue) => {
          const { __typename, ...pair } = mapValue;
          return pair;
        }),
      },
    };
  }
  if (def.__typename === 'RawSqlDefinitionMeta') {
    return {
      rawSql: {
        rawSql: def.rawSql,
        fullDataRequired: def.fullDataRequired,
      },
      mappedString: null,
    };
  }
  throw new UnreachableCaseError(def);
};

export const generateUniqueDataModelName = (
  desiredName: string,
  prefixToTry: string | null,
  allOtherNames: string[],
): string => {
  if (!allOtherNames.includes(desiredName)) {
    return desiredName;
  }
  const prefixedDesiredName = prefixToTry
    ? `${prefixToTry}_${desiredName}`
    : desiredName;
  let currentDesiredName = prefixedDesiredName;
  let ix = 0;
  while (allOtherNames.includes(currentDesiredName)) {
    ix += 1;
    currentDesiredName = `${prefixedDesiredName}_${ix}`;
  }
  return currentDesiredName;
};

export const getAllFieldNamesKeysAndColumns = (
  schemaObj: ISchemaDataModelFieldsFragment | null,
  draftFields: IDataModelerDraftFieldData[],
  derivedFields: IDataModelAddDerivedFieldInput[],
  updateInputs: IDataModelUpdateFieldInput[],
): {
  fieldKeys: string[];
  viewColumns: string[];
  fieldNames: string[];
} => {
  const fieldKeys: string[] = [];
  const viewColumns: string[] = [];
  const fieldNames: string[] = [];
  const updateInputsByKey = listToHashMapFromKey(updateInputs, 'key');
  [
    ...(schemaObj?.fact?.fields ?? []),
    ...(schemaObj?.dimensions?.flatMap((d) => d.fields) ?? []),
    ...(schemaObj?.derivedFields ?? []),
  ].forEach((schemaField) => {
    fieldKeys.push(schemaField.key);
    viewColumns.push(schemaField.column);
    const name = updateInputsByKey[schemaField.key]?.name ?? schemaField.name;
    fieldNames.push(name);
  });
  draftFields.forEach((draftField) => {
    const { addInput } = draftField;
    if (addInput) {
      fieldKeys.push(addInput.key);
      viewColumns.push(addInput.viewColumn);
      fieldNames.push(addInput.name ?? addInput.viewColumn);
    }
  });
  derivedFields.forEach((derivedInput) => {
    fieldKeys.push(derivedInput.key);
    viewColumns.push(derivedInput.column);
    fieldNames.push(derivedInput.name ?? derivedInput.column);
  });
  return {
    fieldKeys,
    viewColumns,
    fieldNames,
  };
};

export const typeParamsToTypeParamsInput = (
  params: Partial<IFieldTypeParamsFieldsFragment>,
): IFieldTypeParamsInput => {
  return {
    arrayItemType: params.arrayItemType ?? null,
    customDefinition: customColumnDefinitionToInput(
      params.customDefinition ?? null,
    ),
    fixedTz: params.fixedTz ?? null,
    linearDistribution: params.linearDistribution ?? false,
    mapDefinition: params.mapDefinition
      ? {
          keyType: params.mapDefinition.keyType,
          valueType: params.mapDefinition.valueType,
        }
      : null,
    structDefinition: params.structDefinition
      ? {
          fields: params.structDefinition.fields.map((fdef) => {
            return {
              type: fdef.type,
              name: fdef.name,
            };
          }),
        }
      : null,
  };
};

export const customDefinitionInputToColumnDefinitionMeta = (
  columnName: string,
  customDefinition: ICustomColumnDefinitionInput,
): IColumnDefinitionMetaFieldsFragment | null => {
  const { mappedString, rawSql } = customDefinition;
  return mappedString
    ? {
        __typename: 'MappedStringDefinitionMeta',
        column: columnName,
        mappedColumn: mappedString.mappedColumn,
        mapElse: mappedString.mapElse,
        mapNull: mappedString.mapNull,
        values: mappedString.values.map(({ original, mapped }) => {
          return {
            __typename: 'MappedValuePair',
            original,
            mapped,
          };
        }),
      }
    : rawSql
      ? {
          __typename: 'RawSqlDefinitionMeta',
          column: columnName,
          rawSql: rawSql.rawSql,
          fullDataRequired: rawSql.fullDataRequired,
        }
      : null;
};

export const typeParamsInputToTypeParamsFieldsFragment = (
  columnName: string,
  params: IFieldTypeParamsInput | null,
): IFieldTypeParamsFieldsFragment => {
  const customDefinition: IColumnDefinitionMetaFieldsFragment | null =
    params?.customDefinition
      ? customDefinitionInputToColumnDefinitionMeta(
          columnName,
          params.customDefinition,
        )
      : null;
  const mapDefinition: IMapDefinitionMetaFieldsFragment | null =
    params?.mapDefinition
      ? {
          __typename: 'MapDefinitionMeta',
          ...params.mapDefinition,
        }
      : null;
  const structDefinition: IStructDefinitionMetaFieldsFragment | null =
    params?.structDefinition
      ? {
          __typename: 'StructDefinitionMeta',
          fields: params.structDefinition.fields.map(({ name, type }) => {
            return {
              __typename: 'StructFieldMeta',
              name,
              type,
            };
          }),
        }
      : null;
  return {
    __typename: 'FieldTypeParams',
    arrayItemType: params?.arrayItemType ?? null,
    customDefinition,
    fixedTz: params?.fixedTz ?? null,
    linearDistribution: params?.linearDistribution ?? false,
    mapDefinition,
    structDefinition,
  };
};

export const defaultExtendFieldInput = (
  tableKey: string,
  fieldKey: string,
  tableColumn: ITableColumnFieldsFragment,
  overrideTypeInput: IDataModelFieldTypeInput | null,
  fieldClass: FieldClass,
): IDataModelExtendFactFieldInput => {
  const { __typename, customDefinition, ...typeParams } =
    tableColumn.typeParams;
  const typeParamsInput: IFieldTypeParamsInput =
    typeParamsToTypeParamsInput(typeParams);
  return {
    column: tableColumn.column,
    fieldKey,
    tableKey,
    typeInput: overrideTypeInput
      ? {
          ...overrideTypeInput,
          type: overrideTypeInput.type,
          fieldClass,
          typeParams: {
            ...typeParamsInput,
            ...overrideTypeInput.typeParams,
            customDefinition: customColumnDefinitionToInput(customDefinition),
          },
        }
      : {
          type: tableColumn.type,
          fieldClass,
          typeParams: {
            ...typeParamsInput,
            customDefinition: customColumnDefinitionToInput(customDefinition),
          },
        },
  };
};

export const defaultAddFieldInput = (
  viewKey: string,
  tableKey: string,
  tableColumn: ITableColumnFieldsFragment,
  schemaObj: ISchemaDataModelFieldsFragment | null,
  otherAddFieldInputs: IDataModelerDraftFieldData[],
  draftDerivedFieldInputs: IDataModelAddDerivedFieldInput[],
  updateFieldInpits: IDataModelUpdateFieldInput[],
  fieldClass: FieldClass = FieldClass.Basic,
): IDataModelAddFieldInput => {
  const typeParams: IFieldTypeParamsInput = typeParamsToTypeParamsInput(
    tableColumn.typeParams,
  );
  const { fieldKeys, fieldNames, viewColumns } = getAllFieldNamesKeysAndColumns(
    schemaObj,
    otherAddFieldInputs,
    draftDerivedFieldInputs,
    updateFieldInpits,
  );
  const fieldKey = generateUniqueDataModelName(
    tableColumn.column.toUpperCase(),
    viewKey.toUpperCase(),
    fieldKeys,
  );
  const fieldName = generateUniqueDataModelName(
    tableColumn.column,
    viewKey.toLowerCase(),
    fieldNames,
  );
  const viewColumn = generateUniqueDataModelName(
    tableColumn.column,
    viewKey.toLowerCase(),
    viewColumns,
  );
  return {
    column: tableColumn.column,
    description: tableColumn.description,
    filter: true,
    groupby:
      DATA_MODEL_FIELD_TYPE_CONFIG[tableColumn.type].defaultGroupby &&
      fieldClass !== FieldClass.Subject,
    key: fieldKey,
    name: fieldName,
    tableKey,
    typeInput: {
      type: tableColumn.type,
      fieldClass,
      typeParams,
    },
    viewColumn,
    viewKey,
  };
};

export const extendFieldDataFromAddFieldData = (
  fieldKey: string,
  prev: IDataModelerDraftFieldData,
  addInput: IDataModelAddFieldInput,
  fieldClass: FieldClass,
): IDataModelerDraftFieldData => {
  return {
    viewKey: prev.viewKey,
    tableKey: prev.tableKey,
    column: prev.column,
    addInput: null,
    extendInput: defaultExtendFieldInput(
      prev.tableKey,
      fieldKey,
      prev.column,
      addInput.typeInput,
      fieldClass,
    ),
  };
};

export const addFieldDataFromExtendFieldData = (
  prev: IDataModelerDraftFieldData,
  prevAddInput: IDataModelAddFieldInput,
  extendInput: IDataModelExtendFactFieldInput,
): IDataModelerDraftFieldData => {
  const addInput: IDataModelAddFieldInput = {
    column: extendInput.column,
    description: prevAddInput.description,
    filter: prevAddInput.filter,
    groupby: prevAddInput.groupby,
    key: extendInput.fieldKey,
    name: prevAddInput.name,
    tableKey: extendInput.tableKey,
    typeInput: extendInput.typeInput,
    viewColumn: prevAddInput.viewColumn,
    viewKey: prev.viewKey,
  };
  return {
    viewKey: prev.viewKey,
    tableKey: prev.tableKey,
    column: prev.column,
    addInput,
    extendInput: null,
  };
};

const joinPairsAreDifferent = (
  joinPairs: ITableJoinPairMeta[],
  conditions: IDataModelJoinPairInput[],
) => {
  if (joinPairs.length !== conditions.length) {
    return true;
  }
  for (let i = 0; i < joinPairs.length; i++) {
    if (joinPairs[i].dimTableFieldColumn !== conditions[i].dimTableColumn) {
      return true;
    }
    if (joinPairs[i].factFieldKey !== conditions[i].factFieldKey) {
      return true;
    }
  }
  return false;
};

const temporalConditionsAreDifferent = (
  temporalJoinMeta: ITemporalJoinConditionMeta | null,
  temporalJoinDraftData: ITemporalJoinConditionDraftData | null,
): boolean => {
  if (!!temporalJoinMeta !== !!temporalJoinDraftData) {
    return true;
  }
  if (!!temporalJoinMeta && !!temporalJoinDraftData) {
    return (
      temporalJoinMeta.temporalJoinType !== temporalJoinDraftData.joinType ||
      temporalJoinMeta.factTemporalKey !==
        temporalJoinDraftData.factTemporalKey ||
      (temporalJoinMeta.dimTemporalKeys[0] ?? null) !==
        temporalJoinDraftData.dimTemporalColumn ||
      (temporalJoinMeta.dimTemporalKeys[1] ?? null) !==
        temporalJoinDraftData.dimSecondTemporalColumn
    );
  }
  return false;
};

export const joinConditionIsDirty = (
  joinMeta: ITableJoinMetaFieldsFragment,
  editData?: IJoinDraftData,
): boolean => {
  if (!editData) {
    return false;
  }
  return (
    joinPairsAreDifferent(joinMeta.joinPairs, editData.conditions) ||
    (joinMeta.sqlCondition ?? '') !== (editData.sqlCondition ?? '') ||
    temporalConditionsAreDifferent(
      joinMeta.temporalCondition,
      editData.temporalCondition,
    )
  );
};

const derivedDefinitionIsDirty = (
  input: IDerivedFieldDefinitionInput,
  fieldMeta: ISchemaFieldMetaFieldsFragment,
): boolean => {
  if (
    !fieldMeta.derivedDefinition ||
    fieldMeta.derivedDefinition.__typename !== 'RawSqlDerivedFieldMeta'
  ) {
    return true;
  }
  return (
    input.rawSql.rawSql !== fieldMeta.derivedDefinition.rawSql ||
    input.rawSql.fullDataRequired !==
      fieldMeta.derivedDefinition.fullDataRequired
  );
};

export const fieldIsDirty = (
  fieldMeta: ISchemaFieldMetaFieldsFragment,
  editInput?: IDataModelUpdateFieldInput,
): boolean => {
  return (
    !!editInput &&
    ((!!editInput.name && editInput.name !== fieldMeta.name) ||
      (editInput.description ?? '') !== (fieldMeta.description ?? '') ||
      (editInput.filter !== null && editInput.filter !== fieldMeta.filter) ||
      (editInput.groupby !== null && editInput.groupby !== fieldMeta.groupby) ||
      (editInput.deprecated !== null &&
        editInput.deprecated !== fieldMeta.deprecated) ||
      (!!editInput.derivedDefinition &&
        derivedDefinitionIsDirty(editInput.derivedDefinition, fieldMeta)))
  );
};

export const isSameColumnAndTypeParamsDefinition = (
  tableField: ITableFieldFieldsFragment,
  columnData: ICdwTableColumnData,
): boolean => {
  return (
    tableField.column === columnData.column &&
    tableField.type === columnData.type &&
    isEqual(tableField.typeParams, columnData.tableColumn.typeParams)
  );
};

export const isSameColumnAndTypeInputDefinition = (
  addInput: MaybeNil<IDataModelAddFieldInput>,
  columnData: ICdwTableColumnData,
): boolean => {
  if (!addInput) {
    return false;
  }
  const inputTypeParams = addInput.typeInput.typeParams;
  const columnDataTypeParams = columnData.tableColumn.typeParams;
  const metaFromInput = !!inputTypeParams?.customDefinition
    ? customDefinitionInputToColumnDefinitionMeta(
        addInput.column,
        inputTypeParams.customDefinition,
      )
    : null;
  return (
    addInput.column === columnData.column &&
    addInput.typeInput.type === columnData.type &&
    (inputTypeParams?.arrayItemType ?? null) ===
      columnDataTypeParams.arrayItemType &&
    (inputTypeParams?.fixedTz ?? null) === columnDataTypeParams.fixedTz &&
    isEqual(metaFromInput, columnDataTypeParams.customDefinition)
  );
};

// attempt to auto discover whether a newly mapped fact column is an
// extension to already existing field or will be a new field.
// matching rules are:
//  * has to be same type
//  * if the view column or any pre-existing table column names are the same as the new column name
export const findExtendingFieldKeyAndClass = (
  factViewKey: string,
  columnTableKey: string,
  column: ITableColumnFieldsFragment,
  factFields: ISchemaFieldMetaFieldsFragment[],
  draftFields: IDataModelerDraftFieldData[],
): {
  fieldKey: string | undefined;
  fieldClass: FieldClass;
} => {
  // first check for existing field
  const existingField = factFields.find(
    (factField) =>
      DATA_MODEL_FIELD_TYPE_CONFIG[factField.type].canExtend.includes(
        column.type,
      ) &&
      // XXX ignore if already mapped in this table
      !factField.tableFields.some((tf) => tf.tableKey === columnTableKey) &&
      (factField.column === column.column ||
        (factField.tableFields.length > 0 &&
          factField.tableFields.some(
            (tableField) => tableField.column === column.column,
          ))),
  );
  if (existingField) {
    return {
      fieldKey: existingField.key,
      fieldClass: existingField.fieldClass,
    };
  }
  const addedFactInputsFromOtherTables = draftFields.flatMap((draftField) => {
    if (
      draftField.viewKey === factViewKey &&
      draftField.tableKey !== columnTableKey &&
      draftField.addInput
    ) {
      return [draftField.addInput];
    } else {
      return [];
    }
  });
  const addedFactFieldInput = addedFactInputsFromOtherTables.find(
    (addInput) =>
      DATA_MODEL_FIELD_TYPE_CONFIG[addInput.typeInput.type].canExtend.includes(
        column.type,
      ) &&
      (addInput.viewColumn === column.column ||
        addInput.column === column.column),
  );
  return {
    fieldKey: addedFactFieldInput?.key,
    fieldClass: addedFactFieldInput?.typeInput?.fieldClass ?? FieldClass.Basic,
  };
};

const generateDummyTemporalRefs = (
  viewKey: string,
): IDataModelTemporalFieldRefsFieldsFragment => {
  return {
    __typename: 'DataModelTemporalFieldRefs',
    viewKey,
    ts: {
      __typename: 'DataModelTemporalFieldRef',
      fieldKey: '',
      columnRefs: [],
    },
    date: {
      __typename: 'DataModelTemporalFieldRef',
      fieldKey: '',
      columnRefs: [],
    },
    dayOfWeek: {
      __typename: 'DataModelTemporalFieldRef',
      fieldKey: '',
      columnRefs: [],
    },
    fourWeek: {
      __typename: 'DataModelTemporalFieldRef',
      fieldKey: '',
      columnRefs: [],
    },
    hourOfDay: {
      __typename: 'DataModelTemporalFieldRef',
      fieldKey: '',
      columnRefs: [],
    },
    month: {
      __typename: 'DataModelTemporalFieldRef',
      fieldKey: '',
      columnRefs: [],
    },
    quarter: {
      __typename: 'DataModelTemporalFieldRef',
      fieldKey: '',
      columnRefs: [],
    },
    week: {
      __typename: 'DataModelTemporalFieldRef',
      fieldKey: '',
      columnRefs: [],
    },
    year: {
      __typename: 'DataModelTemporalFieldRef',
      fieldKey: '',
      columnRefs: [],
    },
  };
};

const bigQueryConnectionInputToMeta = (
  input: IBigQueryConnectionInput,
): IBigQueryConnectionMeta => {
  return {
    __typename: 'BigQueryConnectionMeta',
    ...input,
    jdbcUrl: '',
    strategy: StrategyKind.Bigquery,
  };
};

const basicConnectionInputToMeta = (
  input: IBasicConnectionInput,
): IBasicConnectionMeta => {
  let authType: AuthType = AuthType.UserPassword;
  if (input.privateKey) {
    authType = AuthType.UserPrivateKey;
  }

  return {
    __typename: 'BasicConnectionMeta',
    ...input,
    authType,
  };
};

const endDateConfigInputToMeta = (
  input: IEndDateConfigInput,
): IEndDateConfigMeta => {
  switch (input.kind) {
    case null:
    case EndDateConfigKind.Current:
      return {
        __typename: 'CurrentEndDateMeta',
        kind: EndDateConfigKind.Current,
      };
    case EndDateConfigKind.Static:
      return {
        __typename: 'StaticEndDateMeta',
        kind: EndDateConfigKind.Static,
        staticDate: input.staticDate!!,
        staticDateAsLiveStart: input.staticDateAsLiveStart,
      };
    case EndDateConfigKind.Start:
      return {
        __typename: 'StartEndDateMeta',
        kind: EndDateConfigKind.Start,
        startTimeUnit: input.startTimeUnit!!,
      };
    case EndDateConfigKind.Rolling:
      return {
        __typename: 'RollingEndDateMeta',
        kind: EndDateConfigKind.Rolling,
        rollingCount: input.rollingCount!!,
        rollingTimeUnit: input.rollingTimeUnit!!,
      };
    default:
      throw new UnreachableCaseError(input.kind);
  }
};

const dataSourceInputToMeta = (
  dataSource: IDataSourceInput,
  endDateInput: IEndDateConfigInput,
  weekStart: WeekStart,
  timezone: string,
): ISchemaDsConfigFieldsFragment => {
  const connection = dataSource.connection.bigQuery
    ? bigQueryConnectionInputToMeta(dataSource.connection.bigQuery)
    : basicConnectionInputToMeta(dataSource.connection.basic!!);
  const endDate = endDateConfigInputToMeta(endDateInput);
  return {
    __typename: 'SchemaDsConfig',
    path: [],
    connection,
    dateDt: dataSource.dateDt,
    endDate,
    weekStart,
    timezone,
  };
};

const connectionMetaToInput = (
  connection:
    | IBigQueryConnectionMetaFieldsFragment
    | IBasicConnectionMetaFieldsFragment,
): IConnectionInput => {
  if (connection.__typename === 'BigQueryConnectionMeta') {
    return {
      bigQuery: {
        account: connection.account,
        dataset: connection.dataset,
        keyPlain: '',
        projectId: connection.projectId,
        computeProjectId: connection.computeProjectId,
        timeout: connection.timeout ?? 600,
      },
      basic: null,
    };
  }

  return {
    bigQuery: null,
    basic: {
      strategy: connection.strategy,
      jdbcUrl: connection.jdbcUrl,
      username: connection.username,
      password: '',
      privateKey: null,
      timeout: connection.timeout,
      authType: connection.authType,
    },
  };
};

export const endDateMetaToInput = (
  endDateMeta: IEndDateConfigMeta,
): IEndDateConfigInput => {
  const { __typename, ...cleanProps } = endDateMeta;
  const staticDateStr =
    endDateMeta.__typename === 'StaticEndDateMeta'
      ? `${moment(endDateMeta.staticDate).format(INTERNAL_DATE_FORMAT)}`
      : null;
  return {
    // kind: EndDateConfigKind.Current,
    rollingCount: null,
    rollingTimeUnit: null,
    startTimeUnit: null,
    staticDateAsLiveStart: true,
    ...cleanProps,
    staticDate: staticDateStr,
  };
};

export const dataSourceMetaToInput = (
  dsConfig: ISchemaDsConfigFieldsFragment,
): IDataSourceInput => {
  const { connection } = dsConfig;
  return {
    connection: connectionMetaToInput(connection),
    dateDt: dsConfig.dateDt,
    // endDate: endDateMetaToInput(endDate),
  };
};

export const emptyDataSourceInput = (): IDataSourceInput => {
  return {
    connection: {
      bigQuery: null,
      basic: {
        strategy: StrategyKind.Snowflake,
        jdbcUrl: '',
        username: '',
        password: '',
        privateKey: null,
        timeout: null,
        authType: AuthType.UserPassword,
      },
    },
    dateDt: null,
  };
};

export const emptyEndDateInput = (): IEndDateConfigInput => {
  return {
    kind: EndDateConfigKind.Current,
    rollingCount: null,
    rollingTimeUnit: null,
    startTimeUnit: null,
    staticDateAsLiveStart: true,
    staticDate: null,
  };
};

export const generateDummySchemaObject = ({
  schemaKey,
  schemaName,
  dataSource,
  endDate,
  weekStart,
  timezone,
  dataRefresh,
}: {
  schemaKey: string;
  schemaName: string;
  dataSource: IDataSourceInput;
  endDate: IEndDateConfigInput;
  weekStart: WeekStart;
  timezone: string;
  dataRefresh: IDataRefreshConfigMetaFieldsFragment | null;
}): ISchemaDataModelFieldsFragment => {
  const schemaKeyUc = schemaKey.toUpperCase();
  const factKey = `${schemaKeyUc}_FACT`;
  const dsConfig = dataSourceInputToMeta(
    dataSource,
    endDate,
    weekStart,
    timezone,
  );
  return {
    __typename: 'SchemaDataModel',
    key: schemaKeyUc,
    name: schemaName,
    dsConfig,
    eventTemporals: generateDummyTemporalRefs(factKey),
    fact: {
      __typename: 'FactDataModel',
      key: factKey,
      tableKeys: [],
      fields: [],
    },
    derivedFields: [],
    dimensions: [],
    subjects: [],
    sampling: {
      __typename: 'DataModelSamplingConfig',
      default: 100,
      cohort: 100,
    },
    dataRefresh: {
      __typename: 'DataRefreshConfigMeta',
      minimumRefreshSeconds: dataRefresh?.minimumRefreshSeconds ?? 3600,
      lookBackSeconds: dataRefresh?.lookBackSeconds ?? 24 * 3600,
      pfvMaxNumOfValuesPerKey: dataRefresh?.pfvMaxNumOfValuesPerKey ?? 1000,
      pfvStringMaxLength: dataRefresh?.pfvMaxNumOfValuesPerKey ?? 50,
    },
    tables: [],
    installTemporals: null,
    lockedTtlSeconds: 0,
    version: -1,
    funnelPartitions: [],
    disabled: false,
    description: null,
  };
};

export const updateTableKeyPrefix = (
  tableKey: string,
  oldSchemaKey: string,
  newSchemaKey: string,
): string => {
  const prefixWithDot = `${oldSchemaKey}.`;
  if (tableKey.startsWith(prefixWithDot)) {
    return `${newSchemaKey}.${tableKey.substring(prefixWithDot.length)}`;
  } else {
    return tableKey;
  }
};

export const isModifiedDerivedDefinition = (
  columnName: string,
  originalTypeParams: IFieldTypeParamsFieldsFragment | null,
  definition: ICustomColumnDefinitionInput,
) => {
  if (!originalTypeParams) {
    return true;
  }
  const originalDef: IColumnDefinitionMetaFieldsFragment | null =
    originalTypeParams.customDefinition;
  const newDef: IColumnDefinitionMetaFieldsFragment | null =
    customDefinitionInputToColumnDefinitionMeta(columnName, definition);
  return !isEqual(originalDef, newDef);
};
